import React from 'react';
import {Box, Container, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Image from '../components/Image.js';
import BlockGallery from '../components/BlockGallery.js';
import FeedbackCarousel from '../components/FeedbackCarousel.js';
import ContactForm from '../components/ContactForm.js';
import withMainContainer from '../components/WithMainContainer.js';
import Banner from '../components/Banner.js';
import {imagekitStaticURL} from '../util/util.js';
import WhatsAppButton from '../components/WhatsAppButton.js';
import CardContainer from '../components/CardContainer.js';
import SCButton from '../components/SCButton.js';

const GALLERY_ITEMS = [{
    title: 'פגישת ייעוץ אישית',
    link: '/meeting',
    image: 'bg-gallery-roses.png',
}, {
    title: 'ליווי עסקי ואסטרטגיה הוליסטית',
    link: '/counseling',
    image: 'bg-gallery-forest.png',
}, {
    title: 'סדנאות, הרצאות ומעגלים',
    link: '/workshops',
    image: 'bg-gallery-moon.png',
}, {
    title: 'שיעורים מכושפים אחד על אחד',
    link: '/private_lessons',
    image: 'bg-gallery-magical-stuff.png',
}];

const WelcomeSection = ()=><Stack direction="row" flexWrap="wrap" justifyContent="center" alignItems="start" marginTop={3}>
  <Box display="flex" padding="20px" justifyContent="center">
    <Image imagekit_src="profile-pic-1-full.jpg" width="200px" height="300px"
      sx={{objectFit: 'cover', borderRadius: '10px'}}/>
  </Box>
  <Stack direction="column" flexGrow={1} padding="20px" maxWidth="800px" justifyContent="right" gap={3}>
    <Stack direction="column" spacing={2}>
      <Typography>
        היי, אני סימונה צ’רי - מלווה אנשים בתהליכי דיוק, חיבור והגשמה.<br/>
        בעזרת שילוב בין טארוט, נומרולוגיה ואסטרולוגיה לבין ליווי אישי, שיווק ואסטרטגיה עסקית - אני עוזרת לך להבין מה באמת קורה מתחת לפני השטח, ולבנות דרך שמתאימה רק לך.
      </Typography>
      <Typography>
        אני לא מנחשת עתידות, אני עוזרת לך לעצב את המציאות שלך. כי העתיד לא חקוק בסלע - הוא תלוי בבחירות ובמעשים שלנו.<br/>
        כמו שיועץ פיננסי יכול לראות את ההתנהלות שלך וידע "לחזות" ולהזהיר מה יקרה אם תמשיכי כך, גם אני מסתכלת איתך על התמונה השלמה, וחושפת את מה שלא תמיד רואים לבד:<br/>
        ההרגלים, הדפוסים, התחושות שמפעילות אותך - והפוטנציאל שעוד לא מומש, ואיך בדיוק לממש אותו.
      </Typography>
      <Typography>
        השיטה שלי מדויקת, קשובה ונבנית יחד איתך, צעד אחר צעד, כדי להחזיר לך את המושכות לידיים ולנוע קדימה עם ביטחון ובהירות.
      </Typography>
      <Typography>
        אם את או אתה מרגישים תקועים, מחפשים כיוון או פשוט רוצים לחזור לעצמכם - אני כאן כדי ללוות, להאיר ולעזור לך לשנות את מה שכבר לא עובד, ולעצב מציאות מדויקת יותר עבורך.
      </Typography>
      <Typography fontWeight="bold">
        מוכנים להתחיל את השינוי?
      </Typography>
      <Typography>
        קראו על השירותים שאני מציעה ובואו לגלות מה יכול להתאים לכם עכשיו:
      </Typography>
    </Stack>
  </Stack>
</Stack>;

const TopBanner = ()=><Banner
  image={imagekitStaticURL`header-header.png`}
  placeholderColor="#242328"
  backgroundPosition="bottom"
  padding={7}
  width="100%">
  <Typography variant="h3" textAlign="center">
    הגיע הזמן לראות את כל התמונה<br/>
    ולהחזיר לעצמך את הכוח
  </Typography>
</Banner>;

const TarotSpreadSection = ()=>{
    let theme = useTheme();
    return <Stack sx={{position: 'relative', width: '100%', height: '400px'}}>
      <video autoPlay muted loop
        style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, objectFit: 'cover'}}>
        <source src={imagekitStaticURL`tarotspread.mp4`} type="video/mp4"/>
      </video>
      <Box sx={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'}}/>
      <Container maxWidth="lg" sx={{zIndex: 3, height: '100%'}}>
        <Stack direction="column" gap={4} justifyContent="center" alignItems="center" height="100%">
          <Typography variant="h4" textAlign="center"
            sx={{color: theme.palette.common.white, textShadow: '0 0 4px black, 4px 4px 4px rgba(0, 0, 0, 0.5)'}}>
            בא לך לקבל מסר יומי מקלפי הטארוט?
          </Typography>
          <SCButton text="לבחירת קלף" variant="outlined" href="/tarot-spread" size="large"
            sx={{color: theme.palette.common.white, borderColor: theme.palette.common.white}}/>
        </Stack>
      </Container>
    </Stack>;
};

const TarotMailSection = ()=>{
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    return <Stack direction="column" width="100%"
      padding={5} sx={{backgroundColor: 'primary.light'}}>
      <CardContainer sx={{margin: '0 auto'}}>
        <Stack direction="row" flexWrap="wrap" alignItems="center">
          <Stack>
            <Box component="img" width={sm ? '100%' : '250px'} height="250px"
              src={imagekitStaticURL`tarot-cards-bg.png`}
              sx={{objectFit: 'cover'}}/>
          </Stack>
          <Stack direction="column" gap={3} alignItems="start" padding={3}>
            <Typography variant="h5">
              מסר שבועי במייל
            </Typography>
            <Typography>
              הירשמו וקבלו ממני מסר אישי במתנה כל יום במשך שבוע למייל
            </Typography>
            <SCButton text="אני רוצה מסר" variant="outlined" size="large" href="/tarotmail/simona"
              icon={<ChevronLeftIcon/>} iconPlacement="right"/>
          </Stack>
        </Stack>
      </CardContainer>
    </Stack>;
};

const Home = ()=>{
    let theme = useTheme();
    return <Stack direction="column" justifyContent="center" alignItems="center">
      <TopBanner/>
      <Container maxWidth="lg">
        <WelcomeSection/>
      </Container>
      <Container maxWidth="lg">
        <Stack marginBottom={5} marginTop={3}>
          <BlockGallery items={GALLERY_ITEMS}/>
        </Stack>
      </Container>
      <TarotSpreadSection/>
      <Stack width="100%" sx={{backgroundColor: theme.palette.primary.dark}}>
        <Container maxWidth="lg">
          <Typography color={theme.palette.primary.contrastText} align="center" variant="h3" marginTop={4}>
            מה לקוחות מספרים עליי
          </Typography>
          <FeedbackCarousel tags="home-feedback"/>
        </Container>
      </Stack>
      <TarotMailSection/>
      <Container maxWidth="lg">
        <Stack direction="column" gap={3} marginTop={4}
          justifyContent="center" alignItems="center">
          <Typography variant="h5" textAlign="center">
            לא בטוחים מאיפה להתחיל? אני כאן כדי לעזור
          </Typography>
          <WhatsAppButton text="שלחו לי הודעה בוואצאפ" variant="contained"/>
          <Typography>או</Typography>
          <Stack direction="column" gap={3} padding={4}
            justifyContent="center" alignItems="center"
            sx={{border: '1px solid #ccc', borderRadius: 8}}>
            <Typography variant="h6">
              צרו קשר
            </Typography>
            <ContactForm/>
          </Stack>
        </Stack>
      </Container>
    </Stack>;
};

export default withMainContainer(Home, {headerMargin: 0, footerMargin: 4});
